import { useState, useEffect } from 'react';
import { getAllCompanyByUserRequest, motorcadeCarList } from '@/service/fleetRisk';

interface CarOption {
    label: string;
    value: string;
}

interface CompanyItem {
    name: string;
    [key: string]: any;
}

export interface UseFleetAndCarSelectionReturn {
    allCompanyList: CompanyItem[];
    carOptions: CarOption[];
    loading: boolean;
    fetchCarList: (selectedFleets: string[], insuranceId?: number) => Promise<void>;
}

/**
 * 车队和车辆选择的自定义hook
 * 提供车队列表获取、车辆选项管理和车队/服务商变化处理功能
 */
export const useFleetAndCarSelection = (): UseFleetAndCarSelectionReturn => {
    const [allCompanyList, setAllCompanyList] = useState<CompanyItem[]>([]); // 车队列表
    const [carOptions, setCarOptions] = useState<CarOption[]>([]); // 车辆选项列表
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // 获取当前用户权限下的所有车队
        setLoading(true);
        getAllCompanyByUserRequest()
            .then(res => {
                const resData = res?.data?.data || [];
                setAllCompanyList(resData);
            })
            .catch(err => {
                console.error('获取当前用户权限下的所有车队失败', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // 车队或服务商变化时，刷新车辆选项列表
    const fetchCarList = async (selectedFleets: string[], insuranceId?: number): Promise<void> => {
        if (!selectedFleets || selectedFleets.length === 0) {
            // 未选择车队时，清空车辆选项
            setCarOptions([]);
            return;
        }

        try {
            setLoading(true);
            // 获取所有选中车队的车辆（按服务商过滤）
            const promises = selectedFleets.map(fleetName =>
                motorcadeCarList({ cateName: fleetName, insuranceId })
            );

            const responses = await Promise.all(promises);

            // 合并所有车队的车辆数据并去重
            const allCars: any[] = [];
            responses.forEach(res => {
                const carData = res?.data?.data || [];
                allCars.push(...carData);
            });

            // 去重处理（基于车辆ID）
            const uniqueCars = allCars.filter((car, index, self) =>
                index === self.findIndex(c => c.id === car.id)
            );

            // 构建车辆选项
            const newCarOptions = uniqueCars.map(car => ({
                label: car.plate_number,
                value: car.plate_number
            }));

            setCarOptions(newCarOptions);

        } catch (error) {
            console.error('获取车辆列表失败', error);
            setCarOptions([]);
        } finally {
            setLoading(false);
        }
    };

    return {
        allCompanyList,
        carOptions,
        loading,
        fetchCarList
    };
};